import { useCallback, useEffect, useMemo, useState } from 'react';
import { Row, Space, Typography } from 'antd';
import { MinusCircleOutlined, PlusCircleOutlined, TeamOutlined } from '@ant-design/icons';
import Button from 'components/new-ui/base/button/Button';
import { SectionWrapper } from 'components/new-ui/base/wrappers/styles';
import StatusIcon from 'components/new-ui/base/status-icon/StatusIcon';
import { H2 } from 'components/new-ui/base/typography/styles';
import TeamDetails from './TeamDetails';
import UpdateTeamForm from './UpdateTeamForm';
import { useParams } from 'react-router-dom';
import ActivateDeactivateTeamModal from '../../components/ActivateDeactivateTeamModal';
import useGlobalOverlay from 'hooks/useGlobalOverlay';
import Notification from 'components/ui/base/notification/Notification';
import { IndividualType } from 'store/individuals/individual';
import { getIndividualProfilePictureDownloadPreSign } from 'store/individuals/client';
import { SelectOption } from 'components/new-ui/composed/formSelect/FormSelect';
import useTeams from 'hooks/useTeams';
import useBreadcrumb from 'hooks/useBreadcrumb';
import useSession from 'hooks/useSession';
import { hasPermissions } from 'config/roles/hasPermissions';
import { TeamStatuses } from 'store/teams/types';
import { getErrorMessage } from 'utils/getErrorMessage';

const DetailsTab = () => {
    const { userInfo } = useSession();
    const { teamId } = useParams();
    const { showGlobalOverlay, hideGlobalOverlay } = useGlobalOverlay();
    const { deactivateTeam, team, activateTeam, getTeamById } = useTeams(teamId);
    const { setBreadcrumbRoutes } = useBreadcrumb();
    const [openDeactivateTeamModal, setOpenDeactivateTeamModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [profilePictureUrl, setProfilePictureUrl] = useState<string | null>(null);

    const canUpdateTeams = hasPermissions(userInfo, 'teams', 'UPDATE');

    const isActive = team?.status === TeamStatuses.ACTIVE || false;

    const teamManager = useMemo(
        () => team?.members.find((m) => m.role === IndividualType.TEAM_MANAGER),
        [team?.members]
    );

    const teamManagerInfo = useMemo(
        () => ({
            name: teamManager?.individual?.fullName ?? '',
            imageUrl: profilePictureUrl ?? '',
            individualId: teamManager?.individual.id,
        }),
        [profilePictureUrl, teamManager?.individual?.fullName, teamManager?.individual.id]
    );

    const teamManagerOptions: SelectOption[] = useMemo(
        () =>
            team?.members
                .filter((m) => m.role === IndividualType.TEAM_MANAGER)
                .map((m) => ({
                    id: m.individual.id,
                    value: m.individual.fullName ?? m.individual.email,
                })) ?? [],
        [team]
    );

    const refetchTeam = useCallback(() => {
        if (teamId) {
            getTeamById(teamId);
        }
    }, [getTeamById, teamId]);

    const handleActivateDeactivate = async () => {
        if (!teamId || !team?.id) {
            Notification({
                type: 'error',
                messageTitle: 'Error',
                description: 'Missing team information',
                placement: 'top',
            });
            return;
        }

        try {
            showGlobalOverlay({ type: 'LoadingOverlay' });
            if (isActive) {
                await deactivateTeam(teamId);
                Notification({
                    type: 'success',
                    messageTitle: 'Success',
                    description: 'Successfully deactivated team',
                    placement: 'top',
                });
            } else {
                await activateTeam(teamId);
                Notification({
                    type: 'success',
                    messageTitle: 'Success',
                    description: 'Successfully activated team',
                    placement: 'top',
                });
            }
        } catch (error) {
            const errorMessage = getErrorMessage(error);
            Notification({
                type: 'error',
                messageTitle: 'Operation Failed',
                description: errorMessage || 'Could not update team status. Please try again.',
                placement: 'top',
            });
        } finally {
            hideGlobalOverlay();
        }

        setOpenDeactivateTeamModal(false);
    };

    useEffect(() => {
        if (!teamManager) return;
        getIndividualProfilePictureDownloadPreSign(teamManager.individual.id).then((res) => setProfilePictureUrl(res));
    }, [teamManager]);

    useEffect(() => {
        if (!team) {
            return;
        }

        setBreadcrumbRoutes([
            { breadcrumbName: 'Home', path: '/home' },
            { breadcrumbName: 'Teams', path: '../teams' },
            { breadcrumbName: team.name, path: '' },
        ]);
    }, [setBreadcrumbRoutes, team]);

    if (!teamId || !team) {
        return null;
    }

    return (
        <>
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <Row>
                    <SectionWrapper>
                        <div style={{ display: 'flex', gap: 24 }}>
                            <StatusIcon icon={<TeamOutlined />} status="warning" />
                            {canUpdateTeams && isEditing ? (
                                <UpdateTeamForm
                                    teamId={team?.id}
                                    teamManagerOptions={teamManagerOptions}
                                    initialValues={{
                                        managerId: teamManagerInfo.individualId ?? '',
                                        market: team?.market ?? null,
                                        name: team?.name ?? null,
                                    }}
                                    onCancel={() => setIsEditing(false)}
                                    onSuccess={() => {
                                        setIsEditing(false);
                                        refetchTeam();
                                    }}
                                />
                            ) : (
                                <TeamDetails
                                    onEdit={canUpdateTeams ? () => setIsEditing(true) : undefined}
                                    name={team.name}
                                    market={team.market}
                                    teamManagerInfo={teamManagerInfo}
                                />
                            )}
                        </div>
                    </SectionWrapper>
                </Row>

                {canUpdateTeams && (
                    <>
                        <Row>
                            <SectionWrapper>
                                <div style={{ display: 'flex', gap: 24 }}>
                                    {isActive ? (
                                        <StatusIcon icon={<MinusCircleOutlined />} status="error" />
                                    ) : (
                                        <StatusIcon icon={<PlusCircleOutlined />} status="default" />
                                    )}
                                    <div style={{ flex: 'auto' }}>
                                        <H2>{isActive ? 'Deactivate Team' : 'Activate Team'}</H2>
                                        <Typography>
                                            {isActive
                                                ? 'You can always activate the team again. Your team information will be stored.'
                                                : 'Team will be activated'}
                                        </Typography>
                                    </div>
                                    <Button
                                        type={isActive ? 'error' : 'primary'}
                                        onClick={() => setOpenDeactivateTeamModal(true)}
                                    >
                                        {isActive ? 'Deactivate Team' : 'Activate Team'}
                                    </Button>
                                </div>
                            </SectionWrapper>
                        </Row>
                    </>
                )}
            </Space>

            {canUpdateTeams && (
                <ActivateDeactivateTeamModal
                    active={isActive}
                    open={openDeactivateTeamModal}
                    onCancel={() => setOpenDeactivateTeamModal(false)}
                    onConfirm={handleActivateDeactivate}
                />
            )}
        </>
    );
};

export default DetailsTab;
